import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Loading from '../Common/Loading';
import Button from '../Common/Button';
import FileUpload from './FileUpload';

import { OmaLummeApp } from '../../omaLummeContext';

// Actions
import { postContactForm } from '../../actions';
// Types
import { ContactInfo } from '../../types/contactInfo';
// Utils
import { validateEmail, validatePhoneNumber } from '../../utils/contact';
import { gaEventIfAllowed } from '../../utils/googleAnalytics';

const ContactForm: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { users } = OmaLummeApp.useState();

  const defaultForm = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
  };

  const { endpoint, userId } = users.activeUser;
  const [formStatus, setFormStatus] = useState<boolean>(true);
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [messages, setMessages] = useState<string[]>([]);
  const [validEmail, setValidEmail] = useState<boolean>(false);
  const [validPhone, setValidPhone] = useState<boolean>(false);
  const [userIdState, setUserIdState] = useState<number>(userId);
  const [form, setForm] = useState<ContactInfo>(defaultForm);

  React.useEffect(() => {
    const checkUser = (): void => {
      if (userId !== userIdState) {
        setUserIdState(userId);
      }
    };
    if (userId !== 0) {
      if (userId !== userIdState) {
        setFormStatus(true);
        checkUser();
      }
    } else {
      if (formStatus === true) {
        setFormStatus(false);
      }
    }
  }, [userId, userIdState, defaultForm, endpoint, formStatus]);

  const updateForm = (
    event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    const { target } = event;
    const { value } = target;
    const fieldName: keyof ContactInfo = target.name as keyof ContactInfo;
    const newForm: ContactInfo = { ...form };

    switch (fieldName) {
      case 'email':
        setValidEmail(validateEmail(value));
        if (!validEmail && !messages.includes(t('common:contactform.email'))) {
          messages.push(t('common:contactform.email'));
          setMessages(messages);
        } else if (validEmail) {
          setMessages(messages.filter((message) => message !== t('common:contactform.email')));
        }
        break;
      case 'phone':
        setValidPhone(validatePhoneNumber(value));
        if (!validPhone && !messages.includes(t('common:contactform.phone'))) {
          messages.push(t('common:contactform.phone'));
          setMessages(messages);
        } else if (validPhone) {
          setMessages(messages.filter((message) => message !== t('common:contactform.phone')));
        }
        break;
    }

    setForm(newForm);
    newForm[fieldName] = value;
  };

  const submitForm = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    setMessages([]);
    setFormLoading(true);

    gaEventIfAllowed({
      category: 'Yhteydenotto',
      action: 'Yhteydenottolomake lähetetty',
    });

    const hasEmptyValue = Object.values(form).some((value) => value === '');
    if (!hasEmptyValue && validEmail && validPhone) {
      const contactFormRequest = await postContactForm(form, userId);

      if (contactFormRequest.status === 200) {
        setForm(defaultForm);
        setMessages([t('common:contactform.success')]);
        setFormLoading(false);
        setFormStatus(false);
      } else if (contactFormRequest.status === 201) {
        setForm(defaultForm);
        setMessages([t('common:contactform.created')]);
        setFormLoading(false);
        setFormStatus(false);
      } else {
        setMessages([t('common:contactform.error')]);
        setFormLoading(false);
      }
    }
    if (hasEmptyValue) {
      messages.push(t('common:contactform.empty'));
      setMessages(messages);
      setFormLoading(false);
    }
    if (!validEmail) {
      messages.push(t('common:contactform.email'));
      setMessages(messages);
      setFormLoading(false);
    }
    if (!validPhone) {
      messages.push(t('common:contactform.phone'));
      setMessages(messages);
      setFormLoading(false);
    }
  };

  return (
    <form id="contact-form" className="contact-form" onSubmit={submitForm}>
      <h2 className="h1">{t('customerService:contactUs.contactFormTitle')}</h2>

      {formLoading && <Loading />}
      {!formStatus && !formLoading && (
        <div className="message" aria-live="polite">
          {messages}
          {userId === 0 && (
            <a
              href="#choose-customership"
              onClick={(e): void => {
                e.preventDefault();
                document.getElementById('choose-customership')!.focus();
              }}
            >{`${t('contracts:contracts.contractsIngressLink').charAt(0).toUpperCase()}${t('contracts:contracts.contractsIngressLink').slice(1)}`}</a>
          )}
          {}
        </div>
      )}
      {formStatus && !formLoading && (
        <div className="form-items">
          {messages.length !== 0 && (
            <div className="message" aria-live="polite">
              {messages.map((message, key) => (
                <p key={key}>{message}</p>
              ))}
            </div>
          )}
          <div className="form-item col-6 col-md-6">
            <label htmlFor="firstname">{t('common:general.firstName')}</label>
            <input type="text" id="firstname" name="firstName" onChange={updateForm} onBlur={updateForm} value={form.firstName} required />
          </div>
          <div className="form-item col-6 col-md-6">
            <label htmlFor="lastname">{t('common:general.lastName')}</label>
            <input type="text" id="lastname" name="lastName" onChange={updateForm} onBlur={updateForm} value={form.lastName} required />
          </div>
          <div className="form-item col-6 col-md-6">
            <label htmlFor="phone">{t('common:general.phone')}</label>
            <input type="tel" placeholder="+358" id="phone" name="phone" onChange={updateForm} onBlur={updateForm} value={form.phone} required />
            <span>{t('common:contactform.phone')}</span>
          </div>
          <div className="form-item col-6 col-md-6">
            <label htmlFor="email">{t('common:general.email')}</label>
            <input type="email" id="email" name="email" onChange={updateForm} onBlur={updateForm} value={form.email} required />
            {messages.includes(t('common:contactform.email')) && <span aria-live="polite">{t('common:contactform.email')}</span>}
          </div>
          <div className="form-item col-12">
            <label htmlFor="message">{t('customerService:contactUs.contactTextArea')}</label>
            <textarea id="message" name="message" onChange={updateForm} onBlur={updateForm} value={form.message} required />
          </div>
          <div className="form-item col-12">
            <FileUpload form={{ form, setForm }} />
          </div>
          <div className="form-item submit">
            <Button className="buttonWhite" type="reset" onClick={(): void => setForm(defaultForm)}>
              {t('customerService:contactUs.resetButton')}
            </Button>
            <Button type="submit">{t('customerService:contactUs.submitButton')}</Button>
          </div>
        </div>
      )}
    </form>
  );
};

export default ContactForm;
